import { useEffect, useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import Form, { FormItem, validationRules } from 'components/Base/Form';
import { Col, Row } from 'components/Base/Grid';
import { TextField, TextArea } from 'components/Base/TextField';
import { AdaptiveDatePicker } from 'components/Base/DatePicker';
import { RadioGroup, Radio } from 'components/Base/Radio';
import ImageUploader from 'components/Base/ImageUploader';
import { Paragraph, Text } from 'components/Base/Typography';
import RadioGroupSwitcher from 'components/Base/RadioGroupSwitcher';

import {
  useGetAnimalBreedsQuery,
  useGetAnimalColorsQuery,
  useGetAnimalSpeciesQuery,
} from 'redux/apiSlice/animal.slice';
import Select from 'components/Base/Select';
import { TOptionTypeUi } from 'data/types/generalTypes';
import {
  disabilityDefinition,
  isDisabilityObservableLabel,
  yesNoOptions,
  yesNoTrueFalseOptions,
} from 'data/datas/genericData';
import { Colors } from 'core/CssVariables';
import { scrollToRef } from 'helpers/utils';
import {
  animalFormFields,
  genderOptions,
  typeDropdownItems,
} from './AnimalForm.data';
import { EAnimalInfoConfig } from 'data/types/animalFieldVisibility.types';
import useAnimalFieldVisibility from 'hooks/useAnimalFieldVisibility';
import { animalFormInitialValues } from './animalFormInitialValues';
import { TAnimalFormProps } from './AnimalForm.types';
import { EAnimalTypesUI } from '../../../../../../../../../data/types/animal.types';
import DisabilityOptions from 'components/Shared/DisabilityOptions';

const {
  profilePicture,
  name,
  type,
  birthDate,
  species,
  breed,
  primaryColor,
  secondaryColor1,
  secondaryColor2,
  weight,
  gender,
  neutered,
  houseTrained,
  isAggresive,
  aggressionInfo,
  microchipNumber,
  trainedFor,
  requiredBecauseOfDisability,
  moveInDate,
  observableDisability,
} = animalFormFields;

// react-select stamps this on an option the user typed rather than picked. The dangling
// underscores are its API, not a naming choice of ours.
const isFreshlyTypedOption = (option?: TOptionTypeUi | null) =>
  !!option?.__isNew__;

const AnimalForm = ({
  animalData,
  formObj,
  actionType,
  animalType: selectedAnimalType,
  hasActiveResidency,
  residencyMoveInDate,
  tenantResidencyId,
}: TAnimalFormProps) => {
  const moveInDateFormItemRef = useRef<HTMLDivElement>(null);
  const { form } = formObj;
  const { setFieldsValue } = form;
  const [animalGender, setAnimalGender] = useState('');
  const [animalType, setAnimalType] = useState<TOptionTypeUi | null>(null);
  const [isAnimalAggresive, setIsAnimalAggresive] = useState(
    !!animalData?.aggressiveBehavior.incidentDetails,
  );
  const [hasMicrochip, setHasMicrochip] = useState(false);
  const [isDateRequired, setIsDateRequired] = useState(false);
  const { data: animalSpecies } = useGetAnimalSpeciesQuery();
  const { data: animalBreeds, isFetching: breedsLoading } =
    useGetAnimalBreedsQuery(
      {
        speciesId: isFreshlyTypedOption(animalType)
          ? undefined
          : animalType?.value || undefined,
      },
      { skip: !animalType?.value || isFreshlyTypedOption(animalType) },
    );
  const { data: animalColors } = useGetAnimalColorsQuery();
  // Fields the property chose to hide are not rendered here. Name, type, species and move-in date
  // are never hidden — they gate the application, so a resident who could not see them could never
  // finish it. Nothing here affects the animal profile page, which shows every field regardless.
  //
  // Note what this does NOT do: every hideable row below is `{!isHidden(X) && <FormItem .../>}`, so
  // a hidden field is unmounted and never registered with rc-field-form. It therefore drops out of
  // validateFields() entirely. Its stored value survives only because
  // CreateAnimalModal.handleSubmit merges form.getFieldsValue(true) underneath the validated
  // values — completing an animal is not a patch, and the backend assigns breed, microchip,
  // aggression and house-trained unconditionally. THE CONDITIONAL RENDER IS NOT SELF-SUFFICIENT:
  // that merge must stay, and any other form that hides these fields needs its own.
  const { isHidden } = useAnimalFieldVisibility(tenantResidencyId);

  const [isRequiredForDisability, setIsRequiredForDisability] = useState<
    boolean | undefined
  >();

  const handleSpeciesChange = (item: TOptionTypeUi) => {
    setFieldsValue({ [breed.name]: null });
    setAnimalType(item);
  };

  useEffect(() => {
    if (!animalData) {
      return;
    }
    setAnimalType(animalData.species || null);
    // animalData only, deliberately. The form instance from useForm is stable, so nothing is
    // stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animalData]);

  const disabilitySectionRef = useRef<HTMLDivElement>(null);

  return (
    <Form
      form={form}
      name="animal"
      autoComplete="off"
      requiredMark={false}
      initialValues={animalFormInitialValues(animalData)}
      id="create-animal"
      scrollToFirstError
    >
      {!isHidden(EAnimalInfoConfig.ANIMAL_PHOTO) && (
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <FormItem name={profilePicture.name} className="mb-2">
              <ImageUploader
                url={animalData?.profilePicture || ''}
                width={92}
                height={92}
              />
            </FormItem>
          </Col>
        </Row>
      )}
      {actionType === 'complete' && (
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <FormItem {...type}>
              <Select options={typeDropdownItems} isDisabled />
            </FormItem>
          </Col>
        </Row>
      )}
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <FormItem name={name.name} label={name.label} rules={name.rules}>
            <TextField placeholder={name.placeholder} />
          </FormItem>
        </Col>
        {!isHidden(EAnimalInfoConfig.ANIMAL_BIRTHDAY) && (
          <Col xs={24} md={12}>
            <FormItem name={birthDate.name} label={birthDate.label}>
              <AdaptiveDatePicker
                placeholder={birthDate.placeholder}
                disabledDate={date => date > moment()}
              />
            </FormItem>
          </Col>
        )}
      </Row>
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <FormItem name={species.name} label={species.label}>
            <Select
              isClearable={false}
              options={animalSpecies || []}
              onChange={item => {
                const newItem = item as unknown as TOptionTypeUi;
                handleSpeciesChange(newItem);
              }}
            />
          </FormItem>
        </Col>
        {!isHidden(EAnimalInfoConfig.ANIMAL_BREED) && (
          <Col xs={24} md={12}>
            <FormItem name={breed.name} label={breed.label}>
              <Select
                isCreatable={{
                  skipConfirmation: true,
                }}
                isClearable
                options={
                  animalType && !isFreshlyTypedOption(animalType)
                    ? animalBreeds || []
                    : []
                }
                placeholder={breed.placeholder}
                isLoading={breedsLoading}
              />
            </FormItem>
          </Col>
        )}
      </Row>
      {!isHidden(EAnimalInfoConfig.ANIMAL_COLOR) && (
        <Row gutter={[16, 0]}>
          <Col xs={24} md={8}>
            <FormItem name={primaryColor.name} label={primaryColor.label}>
              <Select
                isCreatable={{
                  skipConfirmation: true,
                }}
                isClearable
                options={animalColors || []}
                placeholder={primaryColor.placeholder}
              />
            </FormItem>
          </Col>
          <Col xs={24} md={8}>
            <FormItem name={secondaryColor1.name} label={secondaryColor1.label}>
              <Select
                isClearable
                options={animalColors || []}
                placeholder={secondaryColor1.placeholder}
              />
            </FormItem>
          </Col>
          <Col xs={24} md={8}>
            <FormItem name={secondaryColor2.name} label={secondaryColor2.label}>
              <Select
                isClearable
                options={animalColors || []}
                placeholder={secondaryColor2.placeholder}
              />
            </FormItem>
          </Col>
        </Row>
      )}
      <Row gutter={[16, 0]}>
        {!isHidden(EAnimalInfoConfig.ANIMAL_WEIGHT) && (
          <Col xs={24} md={12}>
            <FormItem {...weight}>
              <TextField placeholder={weight.placeholder} type="number" />
            </FormItem>
          </Col>
        )}
        {!isHidden(EAnimalInfoConfig.MICROCHIP_NUMBER) && (
          <Col xs={24} md={12}>
            <FormItem
              name={microchipNumber.name}
              label={microchipNumber.label}
              rules={hasMicrochip ? microchipNumber.rules : undefined}
            >
              <TextField
                onChange={e => setHasMicrochip(!!e.target.value)}
                placeholder={microchipNumber.placeholder}
                type="number"
              />
            </FormItem>
          </Col>
        )}
      </Row>

      {(!isHidden(EAnimalInfoConfig.ANIMAL_GENDER) ||
        !isHidden(EAnimalInfoConfig.SPAYED_NEUTERED)) && (
        <Row gutter={[16, 0]}>
          <Col xs={24}>
            <Row align="bottom">
              {!isHidden(EAnimalInfoConfig.ANIMAL_GENDER) && (
                <FormItem
                  name={gender.name}
                  label={gender.label}
                  rules={gender.rules}
                  className="mr-6"
                >
                  <RadioGroupSwitcher
                    options={genderOptions}
                    onChange={e => setAnimalGender(e.target.value)}
                  />
                </FormItem>
              )}
              {!isHidden(EAnimalInfoConfig.SPAYED_NEUTERED) && (
                <Row align="middle">
                  <Paragraph fontLevel={5} className="mb-0 mr-2 mb-6">
                    {`Is your animal ${
                      animalGender === 'female' ? 'Spayed' : 'Neutered'
                    }?`}
                  </Paragraph>
                  <FormItem name={neutered.name} rules={neutered.rules}>
                    <RadioGroup>
                      {yesNoOptions?.map(item => (
                        <Radio
                          value={item.value}
                          label={item.label}
                          key={item.value}
                        />
                      ))}
                    </RadioGroup>
                  </FormItem>
                </Row>
              )}
            </Row>
          </Col>
        </Row>
      )}
      {!isHidden(EAnimalInfoConfig.HOUSE_TRAINED) && (
        <Row gutter={[16, 0]}>
          <Col xs={24}>
            <Row align="middle">
              <Paragraph fontLevel={5} className="mb-0 mr-2 mb-6">
                Is your animal house trained ?
              </Paragraph>
              <FormItem name={houseTrained.name} rules={houseTrained.rules}>
                <RadioGroup>
                  {yesNoOptions?.map(item => (
                    <Radio
                      value={item.value}
                      label={item.label}
                      key={item.value}
                    />
                  ))}
                </RadioGroup>
              </FormItem>
            </Row>
          </Col>
        </Row>
      )}
      {!isHidden(EAnimalInfoConfig.HISTORY_OF_AGGRESSION) && (
        <Row gutter={[16, 0]}>
          <Col xs={24}>
            <Row align="middle">
              <Paragraph fontLevel={5} className="mr-2 mb-6">
                Has your animal ever acted aggressively towards another person?
              </Paragraph>

              <FormItem name={isAggresive.name} rules={isAggresive.rules}>
                <RadioGroup
                  onChange={e => {
                    setIsAnimalAggresive(e.target.value === 'Yes');
                  }}
                >
                  {yesNoOptions?.map(item => (
                    <Radio
                      value={item.value}
                      label={item.label}
                      key={item.value}
                    />
                  ))}
                </RadioGroup>
              </FormItem>
            </Row>
          </Col>
        </Row>
      )}
      {!isHidden(EAnimalInfoConfig.HISTORY_OF_AGGRESSION) && isAnimalAggresive && (
        <Row>
          <Col span={24}>
            <FormItem {...aggressionInfo} className="mb-0">
              <TextArea placeholder={aggressionInfo.placeholder} />
            </FormItem>
          </Col>
        </Row>
      )}
      {hasActiveResidency && (
        <>
          <Row align="middle">
            <Text className="mr-2 mb-6" fontLevel={5}>
              Is this animal moving in with you on the same date
              <Text className="mx-1" color={Colors.Blue} fontLevel={5}>
                ({residencyMoveInDate})
              </Text>
              ?
            </Text>
            <FormItem
              name="moveInDateQuestion"
              rules={[validationRules.required()]}
            >
              <RadioGroup
                onChange={e => {
                  setIsDateRequired(e.target.value === 'No');
                  if (e.target.value === 'Yes') {
                    setFieldsValue({
                      moveInDate: moment(residencyMoveInDate),
                    });
                  } else {
                    form.resetFields([moveInDate.name]);
                    scrollToRef(moveInDateFormItemRef);
                  }
                }}
              >
                <Radio value="Yes" label="Yes" />
                <Radio value="No" label="No" />
              </RadioGroup>
            </FormItem>

            <Col sm={24} md={12} lg={12} ref={moveInDateFormItemRef}>
              <FormItem {...moveInDate} hidden={!isDateRequired}>
                <AdaptiveDatePicker
                  disabledDate={(val: Moment) =>
                    val <= moment(residencyMoveInDate)
                  }
                  placeholder={moveInDate.placeholder}
                />
              </FormItem>
            </Col>
          </Row>
        </>
      )}
      {selectedAnimalType === EAnimalTypesUI.SA && (
        <Row>
          <Paragraph fontLevel={5} className="mb-2">
            What work or task has the animal been trained to perform?
          </Paragraph>
          <Col span={24}>
            <FormItem {...trainedFor}>
              <TextArea placeholder={trainedFor.placeholder} type="text" />
            </FormItem>
          </Col>
        </Row>
      )}
      {selectedAnimalType !== EAnimalTypesUI.PET && (
        <Row gutter={[16, 0]}>
          <Col xs={24}>
            <Row align="middle">
              <Paragraph fontLevel={5} className="mb-0 mr-2 mb-6">
                Is the animal required because of a disability?
              </Paragraph>
              <FormItem
                name={requiredBecauseOfDisability.name}
                rules={[
                  {
                    type: 'boolean',
                    required: true,
                    message: 'This field is required!',
                  },
                ]}
              >
                <RadioGroup
                  onChange={e => {
                    if (selectedAnimalType === EAnimalTypesUI.SA) {
                      setIsRequiredForDisability(e.target.value);
                    }
                    if (e.target.value) {
                      setTimeout(() => scrollToRef(disabilitySectionRef), 0);
                    }
                  }}
                >
                  {yesNoTrueFalseOptions?.map(item => (
                    <Radio
                      value={item.value}
                      label={item.label}
                      key={item.label}
                    />
                  ))}
                </RadioGroup>
              </FormItem>
            </Row>
            <Row align="middle">
              <Paragraph
                fontLevel={5}
                className="mr-2 mb-4"
                color={Colors.DarkGrey}
              >
                {disabilityDefinition}
              </Paragraph>
            </Row>
          </Col>
        </Row>
      )}
      {selectedAnimalType === EAnimalTypesUI.SA && isRequiredForDisability && (
        <div ref={disabilitySectionRef}>
          <Text fontLevel={5} fontWeight={500}>
            {isDisabilityObservableLabel}
          </Text>
          <FormItem {...observableDisability} className="mt-2 mb-0">
            <RadioGroup>
              <DisabilityOptions />
            </RadioGroup>
          </FormItem>
        </div>
      )}
    </Form>
  );
};

export default AnimalForm;
